// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, WrappedComponentProps, injectIntl } from "react-intl";
import { Button } from "react-bootstrap";

import { IAction, ActionType, raiseAction } from "actions";
import { isSet } from "utils";
import { IState } from "states/states";
import { AppState } from "states/schedulerstates";

import AppStateView from "view/comp/AppState";
import { ReactComponent as LoadingAnimation } from "static/logoText.svg";

const CONNECTION_TIMEOUT = 10; // s, after which the offline link will be shown

interface ISiteAnimationExtProps {}
interface ISiteAnimationProps {
  appState: AppState;
  siteConnected: number | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: ISiteAnimationProps = {
    appState: state.scheduler.appState,
    siteConnected: state.cached.selectedSite,
  };
  return props;
};

interface ISiteAnimationState {
  lastState: AppState;
  lastStateSwitch: number | undefined;
}

interface ISiteAnimationDispatch {
  onDisconnectClick: () => void;
  onGoOfflineClick: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISiteAnimationDispatch = {
    onDisconnectClick: () => {
      dispatch(raiseAction(ActionType.ACT_DISCONNECT_SITE));
    },
    onGoOfflineClick: () => {
      dispatch(raiseAction(ActionType.ACT_INIT_DONE));
    },
  };
  return dispatchProps;
};

class SiteAnimationComponent extends React.Component<
  ISiteAnimationProps & ISiteAnimationExtProps & ISiteAnimationDispatch & WrappedComponentProps,
  ISiteAnimationState
> {
  state: ISiteAnimationState = {
    lastState: AppState.INIT,
    lastStateSwitch: undefined,
  };

  public componentDidUpdate() {
    if (this.props.appState !== this.state.lastState) {
      this.setState({ lastState: this.props.appState, lastStateSwitch: Date.now() });
    }
    if (this.props.appState === AppState.LIVE_DATA_SUBSCRIBED) {
      setTimeout(() => {
        // ensure state update to recheck after 1 s
        this.setState({});
      }, 1000);
    }
  }

  public render() {
    return (
      <div style={{ margin: "auto" }}>
        <table>
          <tbody>
            <tr>
              <td>
                <LoadingAnimation />
              </td>
            </tr>
            <tr>
              <td>
                <AppStateView />
              </td>
            </tr>
            <tr>
              <td>
                {this.props.appState >= AppState.SITE_SELECTED &&
                  isSet(this.props.siteConnected) &&
                  this.renderButtons()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  private renderButtons() {
    const isConnectTimedout =
      this.props.appState === AppState.LIVE_DATA_SUBSCRIBED &&
      isSet(this.state.lastStateSwitch) &&
      (Date.now() - this.state.lastStateSwitch) / 1000 > CONNECTION_TIMEOUT;

    return (
      <div className="buttonbox">
        <Button
          variant="secondary"
          id="disconnect"
          type="submit"
          style={isConnectTimedout ? {} : { width: "100%" }}
          onClick={this.props.onDisconnectClick}
        >
          <FormattedMessage id="Disconnect" />
        </Button>
        {isConnectTimedout && (
          <Button
            id="gooffline"
            type="submit"
            variant="danger"
            onClick={this.props.onGoOfflineClick}
          >
            <FormattedMessage id="Connect Offline" />
          </Button>
        )}
      </div>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(SiteAnimationComponent));
